import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { getDocuments } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Search, FolderOpen, Download, FileText, Eye, Newspaper, BookOpen, Calendar, Image, X
} from 'lucide-react';

const CATEGORIES = [
  { value: 'all', label: 'All Categories' },
  { value: 'quality_guidelines', label: 'Quality Guidelines' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'training', label: 'Training Materials' },
  { value: 'call_scripts', label: 'Call Scripts' },
  { value: 'policies', label: 'Policies' },
  { value: 'other', label: 'Other' }
];

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getFileIcon = (mimeType) => {
  if (mimeType?.includes('pdf')) return '📄';
  if (mimeType?.includes('word') || mimeType?.includes('document')) return '📝';
  if (mimeType?.includes('excel') || mimeType?.includes('spreadsheet')) return '📊';
  if (mimeType?.includes('image')) return '🖼️';
  return '📁';
};

const isImageFile = (mimeType) => {
  return mimeType?.includes('image');
};

const isPdfFile = (mimeType) => {
  return mimeType?.includes('pdf');
};

const isOfficeFile = (mimeType) => 
  mimeType?.includes('word') || 
  mimeType?.includes('document') || 
  mimeType?.includes('excel') || 
  mimeType?.includes('spreadsheet') ||
  mimeType?.includes('powerpoint') ||
  mimeType?.includes('presentation');

const getPreviewUrl = (doc) => {
  // Use the preview endpoint for inline viewing (no download dialog)
  return `${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/preview`;
};

const getDownloadUrl = (doc) => {
  return `${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/download`;
};

export default function UserRepository() {
  const { user, userUrl } = useOutletContext();
  const [documents, setDocuments] = useState([]);
  const [newsletters, setNewsletters] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [activeTab, setActiveTab] = useState('documents');
  const [previewNewsletter, setPreviewNewsletter] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        // Fetch all visible documents
        const { data } = await getDocuments({ visible_only: true });
        
        // Separate newsletters from other documents
        const docs = data.filter(doc => doc.category !== 'newsletters');
        const news = data.filter(doc => doc.category === 'newsletters');
        
        setDocuments(docs);
        setNewsletters(news);
      } catch (error) {
        console.error('Failed to fetch documents:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDocuments();
  }, []);

  const filteredDocuments = documents.filter(doc => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!doc.original_filename?.toLowerCase().includes(query) &&
          !doc.description?.toLowerCase().includes(query)) {
        return false;
      }
    }
    if (filterCategory !== 'all' && doc.category !== filterCategory) return false;
    return true;
  });

  const filteredNewsletters = newsletters.filter(news => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!news.original_filename?.toLowerCase().includes(query) &&
          !news.description?.toLowerCase().includes(query)) {
        return false;
      }
    }
    return true;
  });

  const getCategoryLabel = (value) => {
    return CATEGORIES.find(c => c.value === value)?.label || value;
  };

  const getNewsletterThumbnail = (newsletter) => {
    if (isImageFile(newsletter.mime_type)) {
      return `${process.env.REACT_APP_BACKEND_URL}/api/documents/${newsletter.id}/download`;
    }
    // For PDFs and other documents, use a default thumbnail
    return null;
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-repository">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-500 to-cyan-600 rounded-lg p-6 text-white">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-card/20 rounded-lg">
            <FolderOpen className="h-8 w-8" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Document Repository</h1>
            <p className="text-teal-100 mt-1">
              Access quality guidelines, training materials, and quality newsletters
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="documents" className="flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            Quality Documents
          </TabsTrigger>
          <TabsTrigger value="newsletters" className="flex items-center gap-2">
            <Newspaper className="h-4 w-4" />
            Quality Newsletters
          </TabsTrigger>
        </TabsList>

        {/* Quality Documents Tab */}
        <TabsContent value="documents" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search documents..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Documents List */}
          <Card>
            <CardHeader>
              <CardTitle>Available Documents</CardTitle>
              <CardDescription>
                {filteredDocuments.length} document{filteredDocuments.length !== 1 ? 's' : ''} available
              </CardDescription>
            </CardHeader>
            <CardContent>
              {filteredDocuments.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {documents.length === 0 
                      ? "No documents available yet. Check back later."
                      : "No documents match your filters."}
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Document</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead>Uploaded</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDocuments.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{getFileIcon(doc.mime_type)}</span>
                            <div>
                              <p className="font-medium">{doc.original_filename}</p>
                              {doc.description && (
                                <p className="text-xs text-muted-foreground line-clamp-1">{doc.description}</p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{getCategoryLabel(doc.category)}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatFileSize(doc.file_size)}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(doc.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setPreviewDoc(doc)}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            Preview
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Quality Newsletters Tab */}
        <TabsContent value="newsletters" className="space-y-4">
          {/* Search */}
          <Card>
            <CardContent className="p-4">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search newsletters..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </CardContent>
          </Card>

          {/* Newsletters Gallery */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Newspaper className="h-5 w-5 text-cyan-600" />
                Quality Newsletters
              </CardTitle>
              <CardDescription>
                Stay updated with the latest quality insights and company announcements
              </CardDescription>
            </CardHeader>
            <CardContent>
              {filteredNewsletters.length === 0 ? (
                <div className="text-center py-12">
                  <Newspaper className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {newsletters.length === 0 
                      ? "No newsletters available yet. Check back later."
                      : "No newsletters match your search."}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredNewsletters.map((newsletter) => (
                    <div 
                      key={newsletter.id}
                      className="group relative bg-card border rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-pointer"
                      onClick={() => setPreviewNewsletter(newsletter)}
                    >
                      {/* Thumbnail */}
                      <div className="aspect-[3/4] bg-gradient-to-br from-muted to-muted/50 relative overflow-hidden">
                        {isImageFile(newsletter.mime_type) ? (
                          <img 
                            src={getNewsletterThumbnail(newsletter)} 
                            alt={newsletter.original_filename}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center">
                            {isPdfFile(newsletter.mime_type) ? (
                              <>
                                <FileText className="h-16 w-16 text-red-400 mb-2" />
                                <span className="text-xs text-muted-foreground font-medium">PDF Document</span>
                              </>
                            ) : (
                              <>
                                <Newspaper className="h-16 w-16 text-cyan-400 mb-2" />
                                <span className="text-xs text-muted-foreground font-medium">Newsletter</span>
                              </>
                            )}
                          </div>
                        )}
                        
                        {/* Hover Overlay */}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Button variant="secondary" size="sm">
                            <Eye className="h-4 w-4 mr-2" />
                            View
                          </Button>
                        </div>
                      </div>
                      
                      {/* Info */}
                      <div className="p-4">
                        <h3 className="font-medium text-sm line-clamp-2 mb-1">
                          {newsletter.original_filename.replace(/\.[^/.]+$/, "")}
                        </h3>
                        {newsletter.description && (
                          <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                            {newsletter.description}
                          </p>
                        )}
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          {new Date(newsletter.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Newsletter Preview Dialog */}
      <Dialog open={!!previewNewsletter} onOpenChange={(open) => !open && setPreviewNewsletter(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Newspaper className="h-5 w-5 text-cyan-600" />
              {previewNewsletter?.original_filename.replace(/\.[^/.]+$/, "")}
            </DialogTitle>
          </DialogHeader>
          
          {previewNewsletter && (
            <div className="space-y-4">
              {/* Preview Content */}
              <div className="bg-muted/50 rounded-lg p-4 max-h-[60vh] overflow-auto">
                {isImageFile(previewNewsletter.mime_type) ? (
                  <img 
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${previewNewsletter.id}/download`}
                    alt={previewNewsletter.original_filename}
                    className="w-full h-auto rounded-lg shadow-lg"
                  />
                ) : isPdfFile(previewNewsletter.mime_type) ? (
                  <iframe
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${previewNewsletter.id}/download`}
                    className="w-full h-[500px] rounded-lg border"
                    title={previewNewsletter.original_filename}
                  />
                ) : (
                  <div className="text-center py-12">
                    <FileText className="h-16 w-16 text-muted-foreground/50 mx-auto mb-4" />
                    <p className="text-muted-foreground">Preview not available for this file type</p>
                  </div>
                )}
              </div>

              {/* Info & Actions */}
              <div className="flex items-center justify-between">
                <div>
                  {previewNewsletter.description && (
                    <p className="text-sm text-muted-foreground">{previewNewsletter.description}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    Uploaded {new Date(previewNewsletter.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })} • {formatFileSize(previewNewsletter.file_size)}
                  </p>
                </div>
                <Button
                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${previewNewsletter.id}/download`, '_blank')}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Document Preview Dialog */}
      <Dialog open={!!previewDoc} onOpenChange={(open) => !open && setPreviewDoc(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {previewDoc?.original_filename}
            </DialogTitle>
            <DialogDescription>
              {getCategoryLabel(previewDoc?.category)} • {formatFileSize(previewDoc?.file_size || 0)}
            </DialogDescription>
          </DialogHeader>
          
          {previewDoc && (
            <div className="flex-1 overflow-auto">
              {isImageFile(previewDoc.mime_type) ? (
                <div className="flex items-center justify-center p-4">
                  <img 
                    src={getPreviewUrl(previewDoc)} 
                    alt={previewDoc.original_filename}
                    className="max-w-full max-h-[60vh] object-contain rounded-lg"
                  />
                </div>
              ) : isPdfFile(previewDoc.mime_type) ? (
                <iframe
                  src={getPreviewUrl(previewDoc)}
                  className="w-full h-[60vh] rounded-lg border"
                  title={previewDoc.original_filename}
                />
              ) : isOfficeFile(previewDoc.mime_type) ? (
                <div className="flex flex-col items-center justify-center p-8 text-center bg-muted/50 rounded-lg">
                  <div className="text-6xl mb-4">{getFileIcon(previewDoc.mime_type)}</div>
                  <h3 className="text-lg font-medium mb-2">{previewDoc.original_filename}</h3>
                  <p className="text-muted-foreground mb-4">
                    Office documents cannot be previewed directly in the browser.
                  </p>
                  <div className="flex gap-2">
                    <Button onClick={() => window.open(getDownloadUrl(previewDoc), '_blank')}>
                      <Download className="h-4 w-4 mr-2" />
                      Download to View
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center p-8 text-center bg-muted/50 rounded-lg">
                  <div className="text-6xl mb-4">{getFileIcon(previewDoc.mime_type)}</div>
                  <h3 className="text-lg font-medium mb-2">{previewDoc.original_filename}</h3>
                  <p className="text-muted-foreground mb-4">
                    This file type cannot be previewed in the browser.
                  </p>
                  <Button onClick={() => window.open(getPreviewUrl(previewDoc), '_blank')}>
                    <Download className="h-4 w-4 mr-2" />
                    Download File
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
